import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import moment from 'moment';
import { Observable, map, forkJoin, finalize } from 'rxjs';
import OrganizationService from 'src/app/shared/services/organization.service';
import DepartmentGroupService from 'src/app/shared/services/department-group.service';
import { DownloadReportRequest } from '../../organization-models';
import { IdTagSelectorComponent } from 'src/app/shared/components/id-tag-selector/id-tag-selector.component';
import { ChargingStationLocation } from 'src/app/features/charging-station-location/charging-station-location-model';
import { IdTag } from 'src/app/features/id-tags/id-tag-model';
import { LocationSelectorComponent } from 'src/app/shared/components/location-selector/location-selector.component';
import { DepartmentGroup } from 'src/app/features/department/departmentGroup.model';
import { Department, DepartmentSearchEvent } from 'src/app/features/department/department.model';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { PageEvent } from '@angular/material/paginator';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { RequiresFeatureRoleDirective } from 'src/app/shared/directives/requires-feature-role.directive';
import { FeedbackInfoComponent } from 'ok-angular-shared/components';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatLabel } from '@angular/material/form-field';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatBadge } from '@angular/material/badge';
import { MatDatepickerToggle, MatDateRangeInput, MatStartDate, MatEndDate, MatDateRangePicker } from '@angular/material/datepicker';
import { FileSaverService } from './filesaver.service';

interface DownloadReportForm {
  selectedReportType: FormControl<ReportTypeEnum>;
  selectedDepartment: FormControl<Department | null>;
  selectedDepartmentGroup: FormControl<DepartmentGroup | null>;
  idTags: FormControl<IdTag[]>;
  locations: FormControl<ChargingStationLocation[]>;
  startDate: FormControl<moment.Moment>;
  endDate: FormControl<moment.Moment>;
}

enum ReportTypeEnum {
  INVOICE,
  INVOICE_FOR_LOCATIONS,
  CONSUMPTION,
  LOCATION,
  SUMMARIZED_INVOICE,
  LOCATION_ACCESSGROUP,
}

@Component({
  selector: 'adm-download-report-dialog',
  templateUrl: './download-report-dialog.component.html',
  styleUrls: ['./download-report-dialog.component.scss'],
  imports: [
    RequiresFeatureRoleDirective,
    FeedbackInfoComponent,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIcon,
    MatLabel,
    MatRadioGroup,
    MatRadioButton,
    MatFormField,
    MatSuffix,
    MatSelect,
    MatOption,
    MatBadge,
    MatDatepickerToggle,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDateRangePicker,
  ],
  providers: [FileSaverService],
})
export class DownloadReportDialogComponent implements OnInit {
  data = inject<{
    organizationId: number;
    organizationName: string;
    departmentId?: number;
    departmentGroupName?: string;
  }>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private fileSaverService = inject(FileSaverService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private organizationService = inject(OrganizationService);
  private departmentService = inject(DepartmentService);
  private departmentGroupService = inject(DepartmentGroupService);
  private destroyRef = inject(DestroyRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  public loading = false;
  public departmentFiltersLoaded = false;
  public departments: Department[] = [];
  public departmentGroups: DepartmentGroup[] = [];
  public reportTypeEnum: typeof ReportTypeEnum = ReportTypeEnum;
  public idTagBadgeCount = 0;
  public locationBadgeCount = 0;
  public roles = this.environmentLoaderService.virtualRoles;
  public readonly isSpecificDepartmentDialog: boolean;

  constructor() {
    // determent if dialog is opened for specific department or department group
    const hasDepartmentId = this.data.departmentId != null;
    const hasDepartmentGroupName = this.data.departmentGroupName != null && this.data.departmentGroupName !== '';
    this.isSpecificDepartmentDialog = hasDepartmentId || hasDepartmentGroupName;
  }

  public downloadReportFormGroup: FormGroup<DownloadReportForm> = new FormGroup<DownloadReportForm>({
    selectedReportType: new FormControl<ReportTypeEnum>(ReportTypeEnum.INVOICE, { nonNullable: true }),
    selectedDepartment: new FormControl<Department | null>(null),
    selectedDepartmentGroup: new FormControl<DepartmentGroup | null>(null),
    idTags: new FormControl<IdTag[]>([], { nonNullable: true }),
    locations: new FormControl<ChargingStationLocation[]>([], { nonNullable: true }),
    startDate: new FormControl(moment().startOf('day'), { nonNullable: true, validators: [Validators.required] }),
    endDate: new FormControl(moment().startOf('day'), { nonNullable: true, validators: [Validators.required] }),
  });

  ngOnInit(): void {
    if (this.isSpecificDepartmentDialog) {
      this.downloadReportFormGroup.controls.selectedDepartment.disable({ emitEvent: false });
      this.downloadReportFormGroup.controls.selectedDepartmentGroup.disable({ emitEvent: false });
    } else {
      this.downloadReportFormGroup.controls.selectedDepartment.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
        if (val) {
          this.downloadReportFormGroup.controls.selectedDepartmentGroup.disable({ emitEvent: false });
        } else {
          this.downloadReportFormGroup.controls.selectedDepartmentGroup.enable({ emitEvent: false });
        }
      });

      this.downloadReportFormGroup.controls.selectedDepartmentGroup.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(val => {
          if (val) {
            this.downloadReportFormGroup.controls.selectedDepartment.disable({ emitEvent: false });
          } else {
            this.downloadReportFormGroup.controls.selectedDepartment.enable({ emitEvent: false });
          }
        });
    }

    forkJoin({
      departments: this.getDepartments(),
      departmentGroups: this.getDepartmentGroups(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ departments, departmentGroups }) => {
        this.departments = departments;
        this.departmentGroups = departmentGroups;

        // Set department from injected data
        if (this.data.departmentId != null) {
          const matchingDepartment = this.departments.find(d => d.id === this.data.departmentId);
          if (matchingDepartment) {
            this.downloadReportFormGroup.controls.selectedDepartment.setValue(matchingDepartment);
          }
        }

        // Set department group from injected data
        if (this.data.departmentGroupName != null && this.data.departmentGroupName !== '') {
          const matchingDepartmentGroup = this.departmentGroups.find(dg => dg.name === this.data.departmentGroupName);
          if (matchingDepartmentGroup) {
            this.downloadReportFormGroup.controls.selectedDepartmentGroup.setValue(matchingDepartmentGroup);
          }
        }

        this.departmentFiltersLoaded = true;
        this.changeDetectorRef.markForCheck();
      });
  }

  get selectedReportType() {
    return this.downloadReportFormGroup.controls.selectedReportType;
  }

  get hasSelectedDepartment() {
    return !!this.downloadReportFormGroup.controls.selectedDepartment.value;
  }
  get hasSelectedDepartmentGroup() {
    return !!this.downloadReportFormGroup.controls.selectedDepartmentGroup.value;
  }

  get departmentFilterMessage(): string | null {
    if (!this.hasSelectedDepartment) return null;

    const type = this.selectedReportType.value;
    if (type === ReportTypeEnum.INVOICE) {
      return 'Filtrerer på flådebiler som afdelingen faktureres for';
    }
    if (type === ReportTypeEnum.INVOICE_FOR_LOCATIONS) {
      return 'Filtrerer på lokationer tilknyttet afdelingen på opladningstidspunkt';
    }
    return null;
  }

  compareDepartmentGroups(dg1: DepartmentGroup, dg2: DepartmentGroup): boolean {
    return dg1 && dg2 ? dg1.id === dg2.id : dg1 === dg2;
  }

  public resetIdTags() {
    this.downloadReportFormGroup.controls.idTags.setValue([]);
    this.idTagBadgeCount = 0;
  }

  resetLocations() {
    this.downloadReportFormGroup.controls.locations.setValue([]);
    this.locationBadgeCount = 0;
  }

  public resetDepartment(event: Event) {
    this.downloadReportFormGroup.controls.selectedDepartment.setValue(null);
    event.stopPropagation();
  }

  public resetDepartmentGroup(event: Event) {
    this.downloadReportFormGroup.controls.selectedDepartmentGroup.setValue(null);
    event.stopPropagation();
  }

  private getDepartments(): Observable<Department[]> {
    const searchEvent = new DepartmentSearchEvent();
    searchEvent.organizationId = this.data.organizationId;
    const pageEvent = new PageEvent();
    pageEvent.pageSize = 1000;
    searchEvent.paging = pageEvent;
    return this.departmentService.getDepartments(searchEvent).pipe(map(response => response.items));
  }

  private getDepartmentGroups(): Observable<DepartmentGroup[]> {
    return this.departmentGroupService.getDepartmentGroups(this.data.organizationId).pipe(map(response => response.items));
  }

  public openLocationSelector() {
    const dialogRef = this.dialog.open(LocationSelectorComponent, {
      data: {
        selectedLocations: this.downloadReportFormGroup.controls.locations.value,
        organizationId: this.data.organizationId,
        departmentId: this.data.departmentId,
      },
      width: '736px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(locations => {
        if (locations) {
          this.downloadReportFormGroup.controls.locations.setValue(locations);
          this.locationBadgeCount = locations.length;
          this.changeDetectorRef.markForCheck();
          return;
        }
      });
  }

  public openIdTagSelector() {
    const dialogRef = this.dialog.open(IdTagSelectorComponent, {
      data: {
        selectedIdTags: this.downloadReportFormGroup.controls.idTags.value,
        organizationId: this.data.organizationId,
        organizationName: this.data.organizationName,
        departmentId: this.data.departmentId,
      },
      width: '856px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(idTags => {
        if (idTags) {
          this.downloadReportFormGroup.controls.idTags.setValue(idTags);
          this.idTagBadgeCount = idTags.length;
          this.changeDetectorRef.markForCheck();
          return;
        }
      });
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
    this.changeDetectorRef.markForCheck();
  }

  public downloadReport() {
    this.setLoading(true);
    const from = moment(this.downloadReportFormGroup.value.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
    const to = moment(this.downloadReportFormGroup.value.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ');

    if (this.selectedReportType.value === ReportTypeEnum.INVOICE) {
      const downloadReportModel: DownloadReportRequest = {
        from: moment(this.downloadReportFormGroup.value.startDate).format('YYYY-MM-DD'),
        to: moment(this.downloadReportFormGroup.value.endDate).format('YYYY-MM-DD'),
        organizationId: this.data.organizationId,
        departmentGroupId: this.downloadReportFormGroup.controls.selectedDepartmentGroup.value?.id,
        departmentId: this.downloadReportFormGroup.controls.selectedDepartment.value?.id,
      };

      this.organizationService
        .downloadInvoiceReport(downloadReportModel)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.setLoading(false))
        )
        .subscribe({
          next: result => {
            const fileName =
              'Faktureringsrapport fra ' +
              moment(downloadReportModel.from).format('DD-MM-YYYY') +
              ' til ' +
              moment(downloadReportModel.to).format('DD-MM-YYYY') +
              '.xlsx';

            this.fileSaverService.saveFile(result, fileName);
          },
          error: () => {
            console.error('Download failed');
          },
        });
    } else if (this.selectedReportType.value === ReportTypeEnum.CONSUMPTION) {
      const downloadReportModel: DownloadReportRequest = {
        from,
        to,
        organizationId: this.data.organizationId,
        chargingStationLocations: this.downloadReportFormGroup.controls.locations.value.map(l => l.identifier),
        idTagIds: this.downloadReportFormGroup.controls.idTags.value.map(l => l.id),
      };

      this.organizationService
        .downloadConsumptionReport(downloadReportModel)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.setLoading(false))
        )
        .subscribe({
          next: result => {
            const fileName =
              'Forbrugsrapport fra ' +
              moment(downloadReportModel.from).format('DD-MM-YYYY') +
              ' til ' +
              moment(downloadReportModel.to).format('DD-MM-YYYY') +
              '.xlsx';

            this.fileSaverService.saveFile(result, fileName);
          },
        });
    } else if (this.selectedReportType.value === ReportTypeEnum.LOCATION) {
      const downloadReportModel: DownloadReportRequest = {
        from,
        to,
        organizationId: this.data.organizationId,
        chargingStationLocations: this.downloadReportFormGroup.controls.locations.value.map(l => l.identifier),
      };

      this.organizationService
        .downloadLocationReport(downloadReportModel)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.setLoading(false))
        )
        .subscribe({
          next: result => {
            const fileName =
              'Forbrugsrapport for lokationer for ' +
              this.data.organizationName +
              ' fra ' +
              moment(downloadReportModel.from).format('DD-MM-YYYY') +
              ' til ' +
              moment(downloadReportModel.to).format('DD-MM-YYYY') +
              '.xlsx';

            this.fileSaverService.saveFile(result, fileName);
          },
        });
    } else if (this.selectedReportType.value === ReportTypeEnum.INVOICE_FOR_LOCATIONS) {
      const downloadReportModel: DownloadReportRequest = {
        from,
        to,
        organizationId: this.data.organizationId,
        chargingStationLocations: this.downloadReportFormGroup.controls.locations.value.map(l => l.identifier),
        departmentGroupId: this.downloadReportFormGroup.controls.selectedDepartmentGroup.value?.id,
        departmentId: this.downloadReportFormGroup.controls.selectedDepartment.value?.id,
      };

      this.organizationService
        .downloadInvoiceForLocationReport(downloadReportModel)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.setLoading(false))
        )
        .subscribe({
          next: res => {
            const fileName =
              'Faktureringsrapport for lokationer for ' +
              this.data.organizationName +
              ' fra ' +
              moment(downloadReportModel.from).format('DD-MM-YYYY') +
              ' til ' +
              moment(downloadReportModel.to).format('DD-MM-YYYY') +
              '.xlsx';
            this.fileSaverService.saveFile(res, fileName);
          },
        });
    } else if (this.selectedReportType.value === ReportTypeEnum.SUMMARIZED_INVOICE) {
      const downloadReportModel: DownloadReportRequest = {
        from,
        to,
        organizationId: this.data.organizationId,
        chargingStationLocations: this.downloadReportFormGroup.controls.locations.value.map(l => l.identifier),
      };

      this.organizationService
        .downloadSummarizedInvoiceReport(downloadReportModel)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.setLoading(false))
        )
        .subscribe({
          next: result => {
            const fileName =
              'Summeret faktureringssrapport for ' +
              this.data.organizationName +
              ' fra ' +
              moment(downloadReportModel.from).format('DD-MM-YYYY') +
              ' til ' +
              moment(downloadReportModel.to).format('DD-MM-YYYY') +
              '.xlsx';

            this.fileSaverService.saveFile(result, fileName);
          },
        });
    }
  }
}
